import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../../components/Flex";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../../../../components/app/DeleteModal";
import ChartTableWrapper from "../../../../../../../../components/app/provider/patient/add-new-patient/ChartTableWrapper";
import { useReduceDisclosure } from "../../../../../../../../hooks/use-reduce-disclosure";
import { useArchiveState } from "../../../../../../../../hooks/useArchiveState";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useArchivePatientAssessment } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-archive-patient-assessment";
import { useGetAllPatientAssessment } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-patient-assessment";
import { useSendPatientAssessments } from "../../../../../../../../queries/patient-chart/assessment/assement-forms/use-send-patient-assessments";
import { formatDateTime } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import SendPatientAssementFormsModal from "./SendPatientAssementFormsModal";

const PatientSendAssessments = () => {
  const [sendAssementFormsDisclosure, _previewIntakeDisc] =
    useReduceDisclosure(2);
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const { mutateAsync: archiveMutation } = useArchivePatientAssessment();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);
  const { patientId } = useParams();
  const [archiveAssessment, setArchiveAssessment] = useState<boolean>(false);
  const { mutateAsync: sendPatientForms } = useSendPatientAssessments();
  const showSnackbar = useSnackbar();

  const { data: patientAssessments, refetch } = useGetAllPatientAssessment({
    patientId: patientId ?? "",
    pagination: pagination,
    xTENANTID: getXtenantId(),
    archive: false,
  });

  const handleResend = (row: any) => {
    const payload = {
      patientAssessments: {
        assessment: [row.name],
        reminderMedium: ["EMAIL"],
      },
      patientIds: [row.patientId],
    };

    sendPatientForms(payload as any)?.then(() => {
      showSnackbar({
        type: "success",
        message: "Assessment resend successfully",
      });
    });
  };

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "name",
      headerName: "Assessment Name",
      flex: 0.6,
      width: 200,
    },
    {
      field: "created",
      headerName: "Sent On",
      flex: 0.5,
      width: 180,
      renderCell: ({ value }) => formatDateTime(value),
    },
    {
      field: "sendBy",
      headerName: "Sent By",
      flex: 0.5,
      width: 150,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "scheduled",
      headerName: "Status",
      flex: 0.5,
      width: 180,
      renderCell: ({ value }) => (value ? "Scheduled" : "Sent"),
    },
    {
      field: "Action",
      headerName: "Actions",
      width: 90,
      renderCell: ({ row }: any) => {
        return (
          <ActionMenu
            options={[
              { value: "resend", label: "Resend" },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
                isDisabled: false,
                hoverMsg: "Make status disable for archive",
              },
            ].filter((option) =>
              row?.scheduled
                ? option.value === "archive"
                : option.value === "resend"
            )}
            onActionClick={(v) => {
              if (v === "resend") {
                handleResend(row);
              } else if (v === "archive") {
                setArchiveAssessment(row?.archive);
                deleteDisc.open(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <Flex
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100vh - 373px)"}
      gap={1}
    >
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(true, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archiveAssessment ? "unarchive" : "archive"
        } this assessment`}
      />
      <SendPatientAssementFormsModal
        disclosureHost={sendAssementFormsDisclosure}
      />
      <ChartTableWrapper small={false}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={patientAssessments?.data?.content ?? []}
          rowCount={patientAssessments?.data?.page?.totalElements ?? 10}
          columns={COLUMNS}
          // sx={{ marginTop: "40px" }}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
        />
      </ChartTableWrapper>
    </Flex>
  );
};

export default PatientSendAssessments;
